// Calorie distribution across the four meals — a centered dialog from Goals.
//
// Steppers, not sliders (U3): ±5% per tap, and the sheet refuses to save
// until the four shares sum to exactly 100. Each row previews what its
// percentage means in kcal at the CURRENT daily budget, so the person edits
// in the unit they feel (a 600-kcal dinner), not an abstraction. "Automatic"
// clears all four back to the app's preset (25/35/30/10).
import { useState } from "react";
import { api } from "../lib/api";
import { useT } from "../lib/i18n";
import { useToast } from "../lib/toast";
import { SheetShell } from "./SheetShell";
import { Button } from "./ui/Button";
import { hapticTap } from "../lib/haptics";
import type { Goals, MealSlot } from "@shared/types";

const SLOTS: MealSlot[] = ["breakfast", "lunch", "dinner", "snack"];
const PRESET: Record<MealSlot, number> = { breakfast: 25, lunch: 35, dinner: 30, snack: 10 };

interface Props {
  goals: Goals;
  onSaved: (g: Partial<Goals>) => void;
  onClose: () => void;
}

export function MealSplitSheet({ goals, onSaved, onClose }: Props) {
  const t = useT();
  const toast = useToast();
  const custom = goals.split_breakfast != null;
  const [pct, setPct] = useState<Record<MealSlot, number>>({
    breakfast: goals.split_breakfast ?? PRESET.breakfast,
    lunch: goals.split_lunch ?? PRESET.lunch,
    dinner: goals.split_dinner ?? PRESET.dinner,
    snack: goals.split_snack ?? PRESET.snack,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(false);

  const sum = SLOTS.reduce((a, s) => a + pct[s], 0);
  const ok = sum === 100;

  function bump(slot: MealSlot, delta: number) {
    hapticTap();
    setPct((p) => ({ ...p, [slot]: Math.min(70, Math.max(0, p[slot] + delta)) }));
  }

  async function write(next: Partial<Goals>) {
    setBusy(true);
    setErr(false);
    try {
      await api.putGoals(next);
      toast.success(t("toast.saved"));
      onSaved(next);
      onClose();
    } catch {
      setErr(true);
      setBusy(false);
    }
  }

  return (
    <SheetShell variant="center" onClose={onClose}>
      <div className="space-y-4 pt-1">
        <div>
          <h2 className="text-lg font-semibold">{t("split.title")}</h2>
          <p className="mt-1 text-sm text-fg-muted leading-relaxed">{t("split.hint")}</p>
        </div>

        <div className="space-y-2">
          {SLOTS.map((slot) => (
            <div key={slot} className="flex items-center gap-3">
              <span className="flex-1 text-base">{t(`meal.header.${slot}`)}</span>
              <span className="w-16 text-right text-sm text-fg-muted tabular-nums">
                {Math.round((goals.daily_calories * pct[slot]) / 100)} kcal
              </span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => bump(slot, -5)}
                  aria-label={t("common.decrease")}
                  className="w-11 h-11 rounded-full bg-surface-strong text-xl leading-none press"
                >−</button>
                <span className="w-12 text-center text-base font-semibold tabular-nums">{pct[slot]}%</span>
                <button
                  type="button"
                  onClick={() => bump(slot, +5)}
                  aria-label={t("common.increase")}
                  className="w-11 h-11 rounded-full bg-surface-strong text-xl leading-none press"
                >+</button>
              </div>
            </div>
          ))}
        </div>

        <p className={`text-sm font-medium tabular-nums ${ok ? "text-success" : "text-danger"}`}>
          {t("split.sum", { sum: String(sum) })}
        </p>
        {err && <p className="text-sm text-danger">{t("auth.genericError")}</p>}

        <Button
          fullWidth
          disabled={!ok || busy}
          onClick={() => write({
            split_breakfast: pct.breakfast, split_lunch: pct.lunch,
            split_dinner: pct.dinner, split_snack: pct.snack,
          })}
        >
          {t("more_goals.save")}
        </Button>
        {custom && (
          <button
            type="button"
            disabled={busy}
            onClick={() => write({ split_breakfast: null, split_lunch: null, split_dinner: null, split_snack: null })}
            className="w-full py-2 text-sm text-fg-muted press"
          >
            {t("split.automatic")}
          </button>
        )}
      </div>
    </SheetShell>
  );
}
